import React, { useEffect, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import axios from "axios";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * Sovereign Social Cloak Redirects · /ig and /fb
 *
 * Director directive 2026.02 (Strike #15): central, brand-controlled
 * social slugs. Frontend OWNS the slug→URL mapping (single source of
 * truth — no env drift). Telemetry posted best-effort to the same
 * `/api/partners/click/...` channel so all outbound clicks land on the
 * Director Bridge HUD stats panel under one schema.
 *
 *  /ig → https://www.instagram.com/empowerinvestgrow/
 *  /fb → https://www.facebook.com/empowerinvestgrow/
 *
 * To add a new social channel: append to SOCIAL_TARGETS and add a
 * <Route path="/<slug>" element={<SocialGo />} /> in App.js.
 */
const SOCIAL_TARGETS = {
    ig: {
        url: "https://www.instagram.com/empowerinvestgrow/",
        label: "Instagram",
    },
    fb: {
        url: "https://www.facebook.com/empowerinvestgrow/",
        label: "Facebook",
    },
};

export default function SocialGo() {
    const { pathname } = useLocation();
    // pathname is "/ig" or "/fb" — strip the leading slash.
    const slug = (pathname || "/").replace(/^\/+/, "").toLowerCase();
    const target = SOCIAL_TARGETS[slug];

    const [status, setStatus] = useState(target ? "redirecting" : "error");

    useEffect(() => {
        if (!target) return undefined;
        // Fire-and-forget telemetry (re-using partner click channel).
        try {
            axios
                .post(`${BACKEND}/api/partners/click/social-${slug}`)
                .catch(() => {});
        } catch {
            /* swallow */
        }
        const t = setTimeout(() => {
            window.location.replace(target.url);
        }, 300);
        return () => clearTimeout(t);
    }, [slug, target]);

    return (
        <div
            data-testid="social-go-frame"
            className="min-h-screen flex items-center justify-center px-6 text-white"
            style={{
                background:
                    "radial-gradient(ellipse at center, #0D1824 0%, #050A0F 70%, #02060A 100%)",
                fontFamily: "'Inter', sans-serif",
            }}
        >
            <div className="max-w-lg w-full text-center">
                <div
                    className="text-[11px] uppercase tracking-[0.4em] mb-6"
                    style={{
                        color: "#00E5FF",
                        fontFamily: "'Montserrat', sans-serif",
                        fontWeight: 800,
                    }}
                    data-testid="social-go-overline"
                >
                    ◢ Sovereign Cloak Routing
                </div>
                <h1
                    className="text-3xl md:text-4xl uppercase font-black tracking-tight"
                    style={{ fontFamily: "'Montserrat', sans-serif", color: "#F8FAFC" }}
                    data-testid="social-go-title"
                >
                    {status === "error" ? "Routing Failure" : "Patching Signal"}
                </h1>
                <p
                    className="mt-4 text-base leading-relaxed"
                    style={{ color: "#829AB1" }}
                    data-testid="social-go-status"
                >
                    {status === "redirecting" && target && (
                        <>
                            Handing off to{" "}
                            <span style={{ color: "#E8C56C", fontWeight: 700 }}>
                                {target.label}
                            </span>{" "}
                            · stand by.
                        </>
                    )}
                    {status === "error" && "Signal not in roster."}
                </p>

                {status === "redirecting" && target && (
                    <div
                        className="mt-10 inline-block px-4 py-2 text-[11px] uppercase tracking-[0.3em]"
                        style={{
                            border: "1px solid #1E3654",
                            color: "#5C7894",
                            fontFamily: "'Montserrat', sans-serif",
                        }}
                    >
                        <a
                            href={target.url}
                            rel="noopener noreferrer"
                            style={{ color: "#E8C56C" }}
                            data-testid="social-go-manual"
                        >
                            Tap if redirect stalls ↗
                        </a>
                    </div>
                )}

                {status === "error" && (
                    <Link
                        to="/"
                        data-testid="social-go-back"
                        className="mt-10 inline-block px-5 py-3 uppercase tracking-[0.25em] text-xs font-bold"
                        style={{
                            fontFamily: "'Montserrat', sans-serif",
                            color: "#E8C56C",
                            border: "1px solid #E8C56C",
                        }}
                    >
                        [ RETURN TO BRIDGE ]
                    </Link>
                )}

                {status !== "error" && (
                    <div className="mt-12 flex items-center justify-center gap-2">
                        <span
                            className="inline-block w-2.5 h-2.5 rounded-full animate-pulse"
                            style={{
                                background: "#00E5FF",
                                boxShadow: "0 0 12px rgba(0,229,255,0.7)",
                            }}
                        />
                        <span
                            className="text-[10px] uppercase tracking-[0.4em]"
                            style={{ color: "#829AB1", fontFamily: "'Montserrat', sans-serif" }}
                        >
                            Cloak Active
                        </span>
                    </div>
                )}
            </div>
        </div>
    );
}
